import { useMutation, useQueryClient } from '@tanstack/react-query';

import { REQUIRED_TYPES } from '~/components/Offers/Detail/OfferBuilder/DealInformation/tabs/campaignSchedules/campaignScheduleImportCandidates';
import { buildImportGroups } from '~/components/Offers/Detail/OfferBuilder/DealInformation/tabs/campaignSchedules/campaignScheduleImportGroups';
import type { CampaignScheduleImportCandidate } from '~/components/Offers/Detail/OfferBuilder/DealInformation/tabs/campaignSchedules/types';

import OffersService from '~/services/OffersService';

import getErrorMessage from '~/utils/getErrorMessage';

import { offerQueryKeys } from './useGetOffer';

export interface CampaignScheduleImportResult {
  candidate: CampaignScheduleImportCandidate;
  success: boolean;
  error?: string;
}

function brandRegionKey(brand: string, region: string) {
  return `${brand}::${region}`;
}

function pushResults(
  results: Array<CampaignScheduleImportResult>,
  candidates: Array<CampaignScheduleImportCandidate>,
  success: boolean,
  error?: string,
) {
  candidates.forEach((candidate) => results.push({ candidate, success, error }));
}

export function useImportCampaignSchedules(offerId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      selectedCandidates: Array<CampaignScheduleImportCandidate>,
    ): Promise<Array<CampaignScheduleImportResult>> => {
      if (!offerId) {
        throw new Error('offerId is required');
      }

      // Sorted by REQUIRED_TYPES dependency depth so a same-batch availability group is persisted
      // before its dependents — svc-offer re-fetches schedules fresh per request.
      const groups = buildImportGroups(selectedCandidates).sort(
        (a, b) => REQUIRED_TYPES[a.type].length - REQUIRED_TYPES[b.type].length,
      );
      const results: Array<CampaignScheduleImportResult> = [];

      for (const group of groups) {
        // updateScheduleList takes explicit ids, not brands/regions, so any number of updates in a batch go
        // out as a single bulk PATCH — batches are split only by marketing_channels value
        for (const batch of group.updateBatches) {
          try {
            await OffersService.updateScheduleList(
              {
                idList: batch.map((candidate) => candidate.conflictingSchedule.id),
                start: group.start,
                end: group.end,
                marketing_channels: batch[0].conflictingSchedule.marketingChannels,
              },
              offerId,
            );
            pushResults(results, batch, true);
          } catch (error) {
            pushResults(results, batch, false, getErrorMessage(error));
          }
        }

        if (group.toCreate.length === 0) {
          continue;
        }

        if (group.canBulkCreate) {
          try {
            const created: Array<{ brand: string; region: string }> = await OffersService.createScheduleList(
              {
                type: group.type,
                start: group.start,
                end: group.end,
                brands: [...new Set(group.toCreate.map((candidate) => candidate.brand))],
                regions: [...new Set(group.toCreate.map((candidate) => candidate.region))],
              },
              offerId,
            );
            // svc-offer silently omits brand/region combos invalid for a brand instead of erroring —
            // diff the response against the request so those omissions aren't reported as successes.
            const createdKeys = new Set(created.map((schedule) => brandRegionKey(schedule.brand, schedule.region)));
            group.toCreate.forEach((candidate) => {
              if (createdKeys.has(brandRegionKey(candidate.brand, candidate.region))) {
                results.push({ candidate, success: true });
              } else {
                results.push({
                  candidate,
                  success: false,
                  error: `Not created — ${candidate.region} may not be a valid region for ${candidate.brand}`,
                });
              }
            });
          } catch (error) {
            pushResults(results, group.toCreate, false, getErrorMessage(error));
          }
          continue;
        }

        for (const candidate of group.toCreate) {
          try {
            await OffersService.createSchedule(
              {
                type: candidate.type,
                brand: candidate.brand,
                region: candidate.region,
                start: candidate.start,
                end: candidate.end,
              },
              offerId,
            );
            results.push({ candidate, success: true });
          } catch (error) {
            results.push({ candidate, success: false, error: getErrorMessage(error) });
          }
        }
      }

      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.detail(offerId) });
      queryClient.invalidateQueries({ queryKey: offerQueryKeys.scheduleLogs(offerId) });
    },
  });
}
